export default {
  article: {
    metaTitle: 'Guía de Basetao Spreadsheet 2026: cómo organizar tus compras',
    metaDescription:
      'Aprende a crear y usar una hoja de cálculo de Basetao para organizar enlaces de productos, vendedores, fotos de QC y detalles de envío en un solo lugar.',
    breadcrumbHome: 'Inicio',
    breadcrumbCurrent: 'Guía de Basetao Spreadsheet',
    title: 'La guía completa de Basetao Spreadsheet (2026)',
    subtitle:
      'Una guía práctica para crear y usar una hoja de cálculo de Basetao para organizar enlaces de productos, vendedores, fotos de QC y detalles de envío.',
    authorLabel: 'Por',
    author: 'Equipo de Basetao Spreadsheet',
    updatedLabel: 'Última actualización',
    updated: 'Agosto de 2026',
    readingTime: '8 min de lectura',
    tocLabel: 'En esta página',
    intro: {
      heading: 'Introducción',
      p1: 'Comprar a través de un agente de compras chino como Basetao abre la puerta a una enorme variedad de productos — desde zapatillas y ropa hasta accesorios y artículos de uso diario. Pero una vez que tu carrito empieza a llenarse, seguir cada artículo, vendedor, precio y foto de QC puede volverse rápidamente abrumador.',
      p2: 'Ahí es donde entra una hoja de cálculo de Basetao. Es una forma sencilla y organizada de registrar todo lo que compras, para que siempre sepas qué has pedido, dónde está y cuánto costó. Esta guía te explica todo lo que necesitas para crear y usar una de forma eficaz.',
    },
    whatIsBasetao: {
      heading: '¿Qué es Basetao?',
      p1: 'Basetao es un agente de compras que ayuda a los compradores internacionales a adquirir artículos de plataformas chinas como Taobao, 1688 y Weidian. Actúa como una capa intermedia: pegas un enlace de producto, el agente compra el artículo por ti, lo almacena en su almacén y luego lo envía a tu país. En el camino puedes solicitar fotos de QC (control de calidad) para confirmar que el artículo se ve bien antes de enviarlo.',
    },
    whatIsSpreadsheet: {
      heading: '¿Qué es una hoja de cálculo de Basetao?',
      p1: 'Una hoja de cálculo de Basetao es un documento compartido — normalmente creado en Google Sheets o Excel — que enumera cada producto que te interesa o que has pedido a través del agente. Suele registrar el enlace del producto, el vendedor, el precio, la talla o el color, el estado de QC y el progreso del envío.',
      p2: 'Piensa en ella como tu centro de control personal para una compra en Basetao. En lugar de hacer malabares con decenas de pestañas del navegador y mensajes de chat, mantienes todo en un solo lugar que puedes actualizar y revisar de un vistazo.',
    },
    why: {
      heading: '¿Por qué usar una hoja de cálculo de Basetao?',
      p1: 'Una hoja bien mantenida ahorra tiempo y evita errores costosos. Estos son los principales beneficios:',
      items: [
        'Lleva el control de muchos artículos a la vez — especialmente útil para pedidos grandes.',
        'Compara precios y vendedores antes de comprometerte con una compra.',
        'Registra los resultados de QC para decidir rápido si conservas o devuelves un artículo.',
        'Supervisa el estado del almacén y del envío sin buscar en los registros del chat.',
        'Calcula un costo total realista, incluyendo las tarifas del agente y el envío.',
      ],
    },
    whatToInclude: {
      heading: 'Qué incluir en tu hoja de cálculo',
      p1: 'Las columnas exactas dependen de cómo compras, pero estos campos son útiles para casi todo el mundo:',
      items: [
        'Enlace del producto — la URL original para reabrir la publicación en cualquier momento.',
        'Nombre del vendedor — para comparar la calidad entre diferentes tiendas.',
        'Precio — en la moneda local y en tu propia moneda.',
        'Talla, color y cantidad — para evitar pedir la variante incorrecta.',
        'Estado de QC y enlace de foto — para seguir qué artículos pasaron la inspección.',
        'Estado del almacén — llegado, almacenado o listo para enviar.',
        'Método de envío y número de seguimiento — para la entrega final.',
      ],
    },
    structure: {
      heading: 'Cómo estructurar tu hoja de cálculo',
      p1: 'Un diseño de columnas claro hace que tu hoja sea fácil de leer y filtrar. Una estructura inicial práctica se ve así:',
      columns: [
        { col: 'Nombre del producto', desc: 'Un nombre corto y claro para encontrar los artículos rápido.' },
        { col: 'Enlace del producto', desc: 'La URL completa para reabrir la publicación cuando sea necesario.' },
        { col: 'Vendedor', desc: 'El nombre de la tienda, útil para comparar la calidad.' },
        { col: 'Precio', desc: 'Precio local y precio convertido en tu moneda.' },
        { col: 'Talla / color', desc: 'La variante exacta que planeas comprar.' },
        { col: 'QC', desc: 'Un rápido sí/no o un enlace a las fotos de QC.' },
        { col: 'Estado', desc: 'Pedido, en el almacén o enviado.' },
      ],
    },
    tips: {
      heading: 'Consejos para mantenerte organizado',
      items: [
        'Usa una fila por artículo para poder filtrar y ordenar fácilmente.',
        'Codifica las filas por color según el estado para ver el progreso de un vistazo.',
        'Conserva el enlace original del producto — las publicaciones pueden cambiar o desaparecer.',
        'Actualiza la hoja justo después de cada acción en lugar de confiar en la memoria.',
        'Haz una copia de seguridad de la hoja o compártela contigo mismo en la nube.',
      ],
    },
    mistakes: {
      heading: 'Errores comunes que debes evitar',
      items: [
        'Saltarte el QC y descubrir problemas solo después de que el artículo se envíe.',
        'Olvidar anotar la talla o el color exactos, lo que lleva a pedidos incorrectos.',
        'No seguir los costos de envío, lo que hace que el total final sea una sorpresa.',
        'Usar nombres de productos poco claros que luego son imposibles de buscar.',
        'Dejar que la hoja se quede desactualizada, de modo que ya no refleje la realidad.',
      ],
    },
    faq: {
      heading: 'Preguntas frecuentes',
      items: [
        {
          q: '¿Una hoja de cálculo de Basetao es lo mismo que el sitio oficial de Basetao?',
          a: 'No. Es una herramienta de organización independiente. No está afiliada ni forma parte del sitio web oficial de Basetao, salvo que se indique explícitamente.',
        },
        {
          q: '¿Necesito una hoja de cálculo para un pedido pequeño?',
          a: 'No estrictamente, pero incluso unos pocos artículos son más fáciles de gestionar cuando los enlaces, precios y estado de QC están todos en un solo lugar.',
        },
        {
          q: '¿Qué herramienta debo usar para crear la hoja de cálculo?',
          a: 'Google Sheets y Excel son ambos populares. Google Sheets es fácil de consultar desde cualquier dispositivo y sencillo de compartir.',
        },
        {
          q: '¿Qué hago si desaparece una publicación de producto?',
          a: 'Mantén el nombre del vendedor y una descripción clara en tu hoja, y contacta al agente con el enlace original si aún lo tienes.',
        },
        {
          q: '¿Este sitio web vende productos directamente?',
          a: 'No. Este sitio es un recurso informativo. Las categorías de productos enlazan al sitio principal donde puedes explorar y comprar.',
        },
      ],
    },
    cta: {
      title: '¿Listo para organizar tu próxima compra en Basetao?',
      desc: 'Explora las categorías de productos seleccionadas o dirígete al sitio principal para recorrer todo el catálogo.',
    },
  },
};